import { z } from "zod";

import { optionalQueryParam } from "../../../public/v1/schemas/common.schemas";
import {
  workspaceFilePathSchema,
  workspaceFolderPathSchema,
  WorkspaceGitReferenceTargetSchema,
  WorkspaceItemSchema
} from "./core";
import { WorkspaceSlugPathVarsSchema } from "./workspace-crud.schemas";

/**
 * The disambiguator every workspace FILE route accepts beside the slug.
 *
 * `optionalQueryParam`, not a bare `.optional()`: all seven schemas extending
 * this object are bound as `Params`, i.e. parsed from the query string, and
 * `qs` turns `?workspaceId=` into `""` rather than dropping the key. A bare
 * `.optional()` feeds that `""` into the uuid check and 400s a request that
 * meant "no disambiguator" — which is what an external v1/MCP/CLI caller sends
 * when it builds the query from an empty variable.
 *
 * The BODY schemas in this file declare their own `workspaceId` and are
 * deliberately left as `.optional()`: on a body an empty string genuinely is a
 * caller error, so refusing it is correct there.
 */
export const WorkspaceIdentityParamsSchema = z.object({
  /** Optional immutable row id used to disambiguate same-slug org/shared workspaces. */
  workspaceId: optionalQueryParam(z.string().uuid())
});

// List folder
export const ListWorkspaceFolderParamsSchema = WorkspaceIdentityParamsSchema.extend({
  /** Empty string (or omitted) lists the workspace root. */
  path: workspaceFolderPathSchema.optional(),
  continuationToken: z.string().optional()
});

/**
 * `GET /public/v1/workspaces/:slug/folder-archive` — the folder to ZIP (root
 * when omitted) and the identity disambiguator. No `continuationToken`: an
 * archive is not paged, it is streamed whole.
 */
export const WorkspaceFolderArchiveParamsSchema = WorkspaceIdentityParamsSchema.extend({
  path: workspaceFolderPathSchema.optional()
});

// Get file (presigned download URL)
export const GetWorkspaceFileParamsSchema = WorkspaceIdentityParamsSchema.extend({
  path: workspaceFilePathSchema
});

export const GetWorkspaceFileResponseSchema = z.object({
  url: z.string().url()
});

/**
 * In-UI preview response. One endpoint, two strategies (the file's extension
 * decides which — see the shared `workspacePreviewFor` map):
 *   - `url`     → an inline presigned URL the browser renders natively
 *   - `content` → UTF-8 text read + capped server-side (1 MB, may be truncated)
 */
export const WorkspaceFilePreviewResponseSchema = z.discriminatedUnion("mode", [
  z.object({
    mode: z.literal("url"),
    url: z.string().url()
  }),
  z.object({
    mode: z.literal("content"),
    content: z.string(),
    /** True when the file exceeded the cap and `content` is a prefix. */
    truncated: z.boolean()
  })
]);

/**
 * Upload file body — typed as raw `FormData` for the frontend client (it
 * builds `new FormData(); append("file", blob); append("path", "...")`).
 * Mirrors the multipart pattern used by the Documents domain. Backend
 * receives the file via `@UploadedFile()` and the `path` field via
 * `@Body("path")`; the path string is validated with
 * `workspaceFilePathSchema.safeParse(path)` at the controller boundary.
 */
export const UploadWorkspaceFileBodySchema = z.custom<FormData>();

/**
 * Bulk upload — multipart with N files keyed `files`, plus `paths` as a
 * JSON-encoded array of workspace-relative destination paths (positional
 * pairing with `files`). Same FormData wrapper as the single upload.
 *
 * Server fans out N parallel SOURCE+MIRROR writes via Promise.allSettled;
 * partial success is supported (per-file status returned).
 */
export const BulkUploadWorkspaceFilesBodySchema = z.custom<FormData>();

/**
 * The `Body` of `POST /public/v1/workspaces/:slug/upload-batch`: the text
 * fields beside the `files` parts, as the handler receives them after multer,
 * so every value is a string. Named `…BodySchema` because the v1 handler parses
 * it from `@Body()` (`v1-schema-parse-source-convention.spec.ts`).
 *
 * `paths` is a JSON-encoded string array, positional with `files`; the handler
 * decodes it and checks each entry against `workspaceFilePathSchema`, because
 * the length check needs the file count this schema cannot see. `noClobber`
 * arrives as the form string `"true"` or `"false"`; any other spelling is a
 * 400, because a safety flag that silently reads as off would replace the
 * files the caller asked it to protect. The
 * `.optional()` sits OUTSIDE the preprocess so the field projects as optional
 * (an `.optional()` or `.default()` inside reads as required to every
 * JSON-Schema consumer — the `isActive` defect the CLI projection records).
 */
export const WorkspaceUploadBatchBodySchema = z.object({
  paths: z.string(),
  workspaceId: z.string().uuid().optional(),
  noClobber: z
    .preprocess((value) => {
      if (value === "true") return true;
      if (value === "false") return false;
      return value;
    }, z.boolean())
    .optional()
});

const BulkUploadResultSuccessSchema = z.object({
  path: z.string(),
  success: z.literal(true),
  size: z.number().int().nonnegative(),
  modifiedAt: z.string().datetime()
});

const BulkUploadResultFailureSchema = z.object({
  path: z.string(),
  success: z.literal(false),
  skipped: z.literal(false),
  error: z.string()
});

/**
 * The file already existed and the caller asked not to replace it
 * (`noClobber`). Neither a success nor a failure: nothing was written and
 * nothing went wrong, so it is counted apart from both.
 */
const BulkUploadResultSkippedSchema = z.object({
  path: z.string(),
  success: z.literal(false),
  skipped: z.literal(true),
  error: z.string()
});

export const BulkUploadWorkspaceFilesResponseSchema = z.object({
  results: z.array(
    z.union([
      BulkUploadResultSuccessSchema,
      BulkUploadResultFailureSchema,
      BulkUploadResultSkippedSchema
    ])
  ),
  successCount: z.number().int().nonnegative(),
  failureCount: z.number().int().nonnegative(),
  skippedCount: z.number().int().nonnegative()
});

export type BulkUploadWorkspaceFilesResponse = z.infer<
  typeof BulkUploadWorkspaceFilesResponseSchema
>;

// Delete file — path via query, 204
export const DeleteWorkspaceFileParamsSchema = WorkspaceIdentityParamsSchema.extend({
  path: workspaceFilePathSchema
});

// Rename file (POST /rename)
export const RenameWorkspaceFileBodySchema = z
  .object({
    oldPath: workspaceFilePathSchema,
    newPath: workspaceFilePathSchema,
    workspaceId: z.string().uuid().optional()
  })
  .refine((d) => d.oldPath !== d.newPath, "oldPath and newPath must differ");

// Create folder
export const CreateWorkspaceFolderBodySchema = z.object({
  path: workspaceFilePathSchema,
  workspaceId: z.string().uuid().optional()
});

// ---------------------------------------------------------------------------
// Git references (Chapter 26) — a DRIVE workspace pointing at a git project
// ---------------------------------------------------------------------------

/**
 * Create or replace a git reference. `path` is where it lives in the
 * workspace; the server appends the `.gitref` marker suffix if it is absent,
 * so `docs/api` and `docs/api.gitref` name the same reference.
 */
export const SetWorkspaceGitReferenceBodySchema = z.object({
  path: workspaceFilePathSchema,
  target: WorkspaceGitReferenceTargetSchema,
  workspaceId: z.string().uuid().optional()
});

export const GetWorkspaceGitReferenceParamsSchema = WorkspaceIdentityParamsSchema.extend({
  path: workspaceFilePathSchema
});

/**
 * The resolved reference. `gitProject` is denormalised alongside the target so
 * opening a reference is ONE request: the client needs the project's name and
 * default branch to render the code browser, and a bare id tells it neither.
 */
export const GetWorkspaceGitReferenceResponseSchema = z.object({
  target: WorkspaceGitReferenceTargetSchema,
  gitProject: z.object({
    id: z.string().uuid(),
    name: z.string(),
    status: z.string(),
    defaultBranch: z.string()
  })
});

export const DeleteWorkspaceGitReferenceParamsSchema = WorkspaceIdentityParamsSchema.extend({
  path: workspaceFilePathSchema
});

// Delete folder — path via query, 204. Folder path may be empty (purge
// workspace root) but typically points at a subfolder.
export const DeleteWorkspaceFolderParamsSchema = WorkspaceIdentityParamsSchema.extend({
  path: workspaceFolderPathSchema
});

// Restore (soft-delete recovery) — `path` is a deleted file OR folder. Uses the
// non-empty file-path schema (it also accepts folder paths): restoring the
// whole workspace root is intentionally not exposed here.
export const RestoreWorkspaceBodySchema = z.object({
  path: workspaceFilePathSchema,
  workspaceId: z.string().uuid().optional()
});

export const RestoreWorkspaceResponseSchema = z.object({
  /** Workspace-relative paths of the files brought back (folder markers excluded). */
  restored: z.array(z.string()),
  /** Convenience count of `restored.length`. */
  count: z.number().int().nonnegative()
});

export type RestoreWorkspaceResponse = z.infer<typeof RestoreWorkspaceResponseSchema>;

// ---------------------------------------------------------------------------
// Version history (GET /:slug/history) and revert (POST /:slug/revert)
// ---------------------------------------------------------------------------

/** `GET /public/v1/workspaces/:slug/history` — one file's versions, newest first. */
export const WorkspaceFileHistoryParamsSchema = WorkspaceIdentityParamsSchema.extend({
  path: workspaceFilePathSchema
});

/**
 * One entry of a file's version history, as the store records it: a `file`
 * version carries bytes; a `delete-marker` is the tombstone a delete writes on
 * top and has none, which is why it is its own member rather than a `file`
 * with a zero size. `isLatest` marks the entry the live key resolves to today
 * — a `delete-marker` there means the file is currently deleted.
 */
export const WorkspaceFileVersionSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("file"),
    versionId: z.string().min(1),
    isLatest: z.boolean(),
    size: z.number().int().nonnegative(),
    modifiedAt: z.string().datetime(),
    /** The store's entity tag for these bytes — the same `etag` a folder listing reports. */
    etag: z.string().min(1)
  }),
  z.object({
    kind: z.literal("delete-marker"),
    versionId: z.string().min(1),
    isLatest: z.boolean(),
    modifiedAt: z.string().datetime()
  })
]);

export const WorkspaceFileHistoryResponseSchema = z.object({
  /** Newest first. Empty when the path never held an object inside the retention window. */
  versions: z.array(WorkspaceFileVersionSchema)
});

/**
 * `POST /public/v1/workspaces/:slug/revert` — copy the named version on top
 * of the live key. A body, not query params: it is a write.
 */
export const WorkspaceRevertBodySchema = z.object({
  path: workspaceFilePathSchema,
  /** A `file` version id from the history; a `delete-marker` id is refused. */
  versionId: z.string().min(1),
  workspaceId: z.string().uuid().optional()
});

/**
 * A revert that found the named version already live writes nothing and says
 * so, rather than refusing: the caller asked for a state and has it, so a
 * script that reverts twice is not an error the second time.
 */
export const WorkspaceRevertResponseSchema = z.discriminatedUnion("outcome", [
  z.object({
    outcome: z.literal("written"),
    path: z.string(),
    /** The version whose bytes are live again. */
    revertedTo: z.string(),
    /** The id of the new version the revert wrote on top. */
    newVersionId: z.string()
  }),
  z.object({
    outcome: z.literal("already-live"),
    path: z.string(),
    revertedTo: z.string()
  })
]);

export type WorkspaceFileVersion = z.infer<typeof WorkspaceFileVersionSchema>;
export type WorkspaceFileHistoryResponse = z.infer<typeof WorkspaceFileHistoryResponseSchema>;
export type WorkspaceRevertResponse = z.infer<typeof WorkspaceRevertResponseSchema>;

// ---------------------------------------------------------------------------
// Mount credentials (POST /:slug/mount-credentials) — a one-hour S3 session
// scoped to exactly one workspace prefix, for a direct mount from the caller's
// own machine. The session is a bearer credential AWS honours until
// `expiresAt`; nothing here can revoke it earlier.
// ---------------------------------------------------------------------------

/** The access a mount credential carries. `read-write` includes delete. */
export const WorkspaceMountAccessSchema = z.enum(["read", "read-write"]);

/**
 * `access` is the CEILING the caller asks for, never a promise: the server
 * grades it down to what the key's scopes, the workspace kind and the shared
 * write grant allow, and the response's `access` says what was granted.
 * A downgrade is a normal response, not a refusal.
 *
 * `.strict()` because the default is the WIDEST grade: a misspelt `access` key
 * stripped in silence would mint read-write for a caller who asked for read.
 * An unknown key is a 400, as on the create and rename bodies.
 */
export const MintWorkspaceMountCredentialsBodySchema = z
  .object({
    workspaceId: z.string().uuid().optional(),
    access: WorkspaceMountAccessSchema.default("read-write")
  })
  .strict();

/**
 * `workspace` is the list item's own fields, so the mint and the list spell one
 * workspace the same way. `bucket` is bounded by the S3 name limit, `prefix` by
 * the slug limit plus its trailing slash, `accessKeyId` by the 16..128 the STS
 * `Credentials` type publishes. STS publishes NO size for `SecretAccessKey` or
 * `SessionToken` (the token grows with the inline session policy), so neither
 * is bounded here: the response interceptor validates this schema after the
 * session exists, and a ceiling AWS never promised would turn a valid mint into
 * a 500.
 */
export const WorkspaceMountCredentialsResponseSchema = z.object({
  workspace: WorkspaceItemSchema.pick({
    id: true,
    slug: true,
    name: true,
    kind: true,
    isShared: true
  }),
  /**
   * The ACTING organization — the one whose key is mounting, which for a shared
   * (ownerless) workspace is not an owner. Present so a client can label the
   * drive with a name the SERVER holds: the CLI's own copy is written into the
   * profile at sign-in and never re-read, so an organization renamed afterwards
   * kept showing its old name on the volume and in the mount list.
   *
   * `name` is nullable because the lookup answers with a Map and an absent id
   * stays absent — a hard-deleted organization yields no name rather than a
   * fabricated one, and the client falls back to whatever it already had.
   */
  organization: z.object({
    id: z.string().min(1).max(64),
    name: z.string().max(256).nullable()
  }),
  /** GRANTED access — may be lower than the `access` the body requested. */
  access: WorkspaceMountAccessSchema,
  storage: z.object({
    bucket: z.string().max(63),
    /** Always `<slug>/`: the one key prefix the credentials reach. */
    prefix: z.string().max(65),
    region: z.string().max(32)
  }),
  credentials: z.object({
    accessKeyId: z.string().min(16).max(128),
    secretAccessKey: z.string().min(1),
    sessionToken: z.string().min(1)
  }),
  expiresAt: z.string().datetime()
});

export type WorkspaceMountAccess = z.infer<typeof WorkspaceMountAccessSchema>;
export type MintWorkspaceMountCredentialsBody = z.infer<
  typeof MintWorkspaceMountCredentialsBodySchema
>;
export type WorkspaceMountCredentialsResponse = z.infer<
  typeof WorkspaceMountCredentialsResponseSchema
>;

// ---------------------------------------------------------------------------
// Search (server-side frontmatter/keyword search — NEX-2407)
// ---------------------------------------------------------------------------

/**
 * One `frontmatter` filter, encoded as `key=value` in the query string (the
 * param is repeatable). Value-only matching is case-insensitive substring,
 * applied to the parsed frontmatter field. The key cannot be empty; the value
 * may be (matches any non-empty value for that key).
 */
const FrontmatterFilterStringSchema = z.string().refine(
  (s) => {
    const eq = s.indexOf("=");
    return eq > 0 && s.slice(0, eq).trim().length > 0;
  },
  { message: "frontmatter filter must be in 'key=value' form with a non-empty key" }
);

/**
 * Search query params. Repeatable `frontmatter` arrives either as a single
 * string (one filter) or an array (many) — normalize to an array. At least one
 * of `query` or `frontmatter` must be present, else the search has no criteria.
 */
export const SearchWorkspaceParamsSchema = WorkspaceIdentityParamsSchema.extend({
  /** Free-text keyword (case-insensitive substring over content, frontmatter, and path). */
  query: z.string().trim().min(1).optional(),
  /** One or more `key=value` frontmatter constraints; ALL must hold. */
  frontmatter: z
    .union([FrontmatterFilterStringSchema, z.array(FrontmatterFilterStringSchema)])
    .transform((v) => (Array.isArray(v) ? v : [v]))
    .optional(),
  /** Restrict the search to a subfolder (workspace-relative). Omit to search the whole workspace. */
  path: workspaceFolderPathSchema.optional(),
  /** Max hits to return (1–200, default 50). Coerced from the query string. */
  limit: z.coerce.number().int().min(1).max(200).optional().default(50)
}).refine((d) => Boolean(d.query) || (d.frontmatter?.length ?? 0) > 0, {
  message: "provide a 'query' and/or at least one 'frontmatter' filter"
});

const WorkspaceSearchHitSchema = z.object({
  /** Workspace-relative path, no leading slash. */
  path: z.string(),
  size: z.number().int().nonnegative(),
  modifiedAt: z.string().datetime(),
  /** Excerpt around the match, or `null` when the match was frontmatter/path only. */
  snippet: z.string().nullable(),
  /** Parsed frontmatter of the doc, or `null` when it has none. */
  frontmatter: z.record(z.string(), z.string()).nullable(),
  /** Where the query/filters matched. */
  matchedIn: z.array(z.enum(["content", "frontmatter", "path"]))
});

export const SearchWorkspaceResponseSchema = z.object({
  results: z.array(WorkspaceSearchHitSchema),
  /** Number of files actually read and inspected. */
  scanned: z.number().int().nonnegative(),
  /** True when the candidate set exceeded the scan cap, so results may be incomplete. */
  truncated: z.boolean()
});

export type SearchWorkspaceParams = z.infer<typeof SearchWorkspaceParamsSchema>;
export type WorkspaceSearchHit = z.infer<typeof WorkspaceSearchHitSchema>;
export type SearchWorkspaceResponse = z.infer<typeof SearchWorkspaceResponseSchema>;

// Re-export shared path-vars schema so consumers don't need to know which file owns it
export { WorkspaceSlugPathVarsSchema };
